import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Typography, useTheme, Tooltip } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import moment from 'moment';
import { getIn, useField, useFormikContext } from 'formik';
import { at } from 'lodash';
import { DatePicker } from '@mui/x-date-pickers';
import { useSelector } from 'react-redux';
import { bloqcibeApi } from '../../../store/slices/apiSlice';
import { useParams } from 'react-router-dom';
import { isNewValidationEnabled } from '../utils/validationService';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const YearPickerField = (props) => {
  const {
    label,
    setFieldValue,
    name,
    fieldKey,
    valueKey,
    disableFutureDates = false,
    disablePastDates = false,
    range = [1901, 2025], // Default year range [minYear, maxYear]
    required,
    requiredDepends,
    requiredValidationValue,
    requiredDependOnFields,
    fieldConfig,
    info,
  } = props;
  const { trialId, trialSiteId, siteInitStep, subjectMasterId } = useParams();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const theme = useTheme();
  const { values, errors, touched } = useFormikContext();
  const [disabled, setDisabled] = useState(false);
  const subject = useSelector((state) => state.subject);
  const [field, meta, helpers] = useField(name);
  const [getSubjectAnswers] = bloqcibeApi.endpoints.getSubjectAnswers.useLazyQuery();
  const [getTrialSiteInfo] = bloqcibeApi.endpoints.getTrialSiteInfo.useLazyQuery();
  const trialSite = useSelector((state) => state.trial?.trialSiteDetail);
  // Cache for API responses
  const apiCacheRef = useRef(new Map());

  function _renderHelperText() {
    const [touched, error] = at(meta, 'touched', 'error');
    if (touched && error) {
      return error;
    } else {
      return ' ';
    }
  }

  const isRequired = useMemo(() => {
    if (Array.isArray(requiredDependOnFields) && requiredDependOnFields.length > 0) {
      for (const dep of requiredDependOnFields) {
        if (dep.requiredDepends) {
          const dependsKey = dep.requiredDepends.includes('.') ? dep.requiredDepends.split('.') : dep.requiredDepends;
          const dependsValue = dep.requiredDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];

          // Check if requiredValidationValue is provided - if so, use exact match validation
          // If not provided, check if any value exists (truthy check)
          if (
            dep.requiredValidationValue !== undefined &&
            dep.requiredValidationValue !== null &&
            dep.requiredValidationValue !== ''
          ) {
            if (dependsValue === dep.requiredValidationValue) {
              return true;
            }
          } else {
            // If requiredValidationValue is not provided, check if any value exists
            if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
              return true;
            }
          }
        }
      }
    } else if (requiredDepends) {
      const dependsKey = requiredDepends.includes('.') ? requiredDepends.split('.') : requiredDepends;
      const dependsValue = requiredDepends?.includes('.') ? values[dependsKey[0]]?.[dependsKey[1]] : values[dependsKey];

      // Check if requiredValidationValue is provided - if so, use exact match validation
      // If not provided, check if any value exists (truthy check)
      if (requiredValidationValue !== undefined && requiredValidationValue !== null && requiredValidationValue !== '') {
        if (dependsValue === requiredValidationValue) {
          return true;
        }
      } else {
        // If requiredValidationValue is not provided, check if any value exists
        if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
          return true;
        }
      }
    }

    if (required) {
      return true;
    }

    return false;
  }, [values, requiredDepends, requiredValidationValue, required, requiredDependOnFields]);

  const isDisabled = useMemo(() => {
    if (props.disabled) {
      return true;
    }

    if (Array.isArray(props.disabledDependsOnFields) && props.disabledDependsOnFields.length > 0) {
      for (const dep of props.disabledDependsOnFields) {
        if (dep.disabledDepends && dep.disabledValidationValue !== undefined) {
          const dependsKey = dep.disabledDepends.includes('.') ? dep.disabledDepends.split('.') : dep.disabledDepends;
          const dependsValue = dep.disabledDepends.includes('.')
            ? values[dependsKey[0]]?.[dependsKey[1]]
            : values[dependsKey];
          if (dependsValue === dep.disabledValidationValue) {
            return true;
          }
        }
      }
    } else if (props.disabledDepends && props.disabledValidationValue !== undefined) {
      const dependsKey = props.disabledDepends.includes('.') ? props.disabledDepends.split('.') : props.disabledDepends;
      const dependsValue = props.disabledDepends?.includes('.')
        ? values[dependsKey[0]]?.[dependsKey[1]]
        : values[dependsKey];
      if (dependsValue === props.disabledValidationValue) {
        return true;
      }
    }
    return false;
  }, [values, props.disabledDepends, props.disabledValidationValue, props.disabled, props.disabledDependsOnFields]);

  useEffect(() => {
    if (props.disabled) {
      setDisabled(true);
    }
  }, [props.disabled]);

  return (
    <>
      <Box
        sx={{
          height: props.justifycontent ? 'auto' : '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: props.justifycontent ? 'start' : 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Typography sx={{ color: '#5b5b5b', fontWeight: 'medium' }} variant="subtitle1" gutterBottom>
            {label}
            {label && isRequired && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
          </Typography>
          {info && (
            <Tooltip
              title={info}
              componentsProps={{
                tooltip: {
                  sx: { whiteSpace: 'pre-line' },
                },
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#0f52b9', ml: 1, fontSize: 18, mb: 1 }} />
            </Tooltip>
          )}
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="YYYY"
            views={['year']}
            minDate={dayjs(`${range[0]}-01-01`)}
            maxDate={dayjs(`${range[1]}-12-31`)}
            onChange={async (value) => {
              if (value === null) {
                await setFieldValue(name, '', true);
                helpers.setTouched(true);
                helpers.setError(undefined);
              } else {
                // Store only the year as YYYY string
                await setFieldValue(name, dayjs(value).format('YYYY'), true);
                helpers.setTouched(true);
                helpers.setError(undefined);
              }
            }}
            value={field.value ? dayjs(field.value, 'YYYY') : null}
            disabled={isDisabled}
            disableFuture={disableFutureDates}
            disablePast={disablePastDates}
            slots={{
              openPickerIcon: CalendarMonthIcon,
            }}
            sx={{ svg: { color: isDisabled ? '#bdbdbd' : theme.palette.primary.light } }}
            slotProps={{
              textField: {
                error: Boolean(meta.touched && meta.error),
                helperText: _renderHelperText(),
                name: name,
                variant: 'outlined',
                fullWidth: true,
                placeholder: 'YYYY',
              },
              actionBar: {
                actions: ['clear', 'cancel'],
              },
            }}
          />
        </LocalizationProvider>
      </Box>
    </>
  );
};

export default React.memo(YearPickerField);

